/**
 * Typen des Mängelkatalogs („Standardmangel per Torte“).
 *
 * Plan: docs/mangelkatalog-plan.md · Vertrag: docs/mangelkatalog-vertrag.md.
 * Diese Datei ist die gemeinsame Schnittstelle aller Pakete — Änderungen nur
 * über den Vertrag.
 */

// ─── Gesamtkatalog (ausgelieferte Quelle) ───────────────────────────────────
//
// Entscheid 28.09.2026: kein Bezug zur Herkunft des Katalogs, keine
// Katalognummer — weder in der Oberfläche noch gespeichert. Ein Eintrag trägt
// nur Überschrift, Beschreibung und die gekürzte Vorschrift. Die volle
// eingelesene Fassung liegt nur bei den Skripten (scripts/mangelKatalog/daten).

/** Ein Abschnittskopf des Gesamtkatalogs (z. B. 7500 „Seitenschutz“). */
export interface KatalogAbschnitt {
    /** Interne Abschnitts-ID, z. B. '7500' — nie anzeigen. */
    id: string;
    titel: string;
    /** Übergeordneter Kopf (z. B. '7500' für '7502'), sonst weggelassen. */
    eltern?: string;
}

/** Ein Eintrag des Gesamtkatalogs: Überschrift, Beschreibung, gekürzte Vorschrift. */
export interface KatalogEintrag {
    /** Interne, eindeutige ID — nie anzeigen, nie an einem Mangel speichern. */
    id: string;
    /** ID des nächstliegenden Abschnittskopfs (z. B. '7502'). */
    abschnitt: string;
    /** Überschrift. */
    titel: string;
    /** Beschreibung. */
    text: string;
    /** Vorschrift, bereits gekürzt (so steht sie im Bericht). */
    vorschrift: string;
}

/** Der Gesamtkatalog für Suche und Admin (src/services/mangelKatalog/quelle/gesamtkatalog.json). */
export interface Gesamtkatalog {
    format: 1;
    abschnitte: KatalogAbschnitt[];
    eintraege: KatalogEintrag[];
}

/** Eintrag des statischen Kernauszugs (nur die Einträge, auf die der Baum verweist). */
export interface KatalogAuszug {
    /** Interne ID — nie anzeigen. */
    id: string;
    abschnitt: string;
    titel: string;
    text: string;
    /** Gekürzte Vorschrift (so steht sie im Bericht). */
    vorschriftKurz: string;
}

// ─── Standard-Baum (Torte) ──────────────────────────────────────────────────

/** Ebene 1 = kategorie, Ebene 2 = gruppe (Unterpunkt), Ebene 3 = mangel (Kachel). */
export type KnotenTyp = 'kategorie' | 'gruppe' | 'mangel';

/** Dringlichkeit eines Standardmangels. Ohne Angabe bleibt die heutige Frist (+14 Tage). */
export type Dringlichkeit = 'sofort' | 'frist';

export type KnotenStatus = 'entwurf' | 'freigegeben';

/**
 * Ein Knoten des Standard-Baums (standard/baum.json) oder ein eigener Knoten
 * der Firma (Overlay `eigene`).
 *
 * IDs: Standard = Punkt-getrennte Slugs [a-z0-9-], z. B. 'absturz',
 * 'absturz.seitenschutz', 'absturz.seitenschutz.fehlt' (≤ 120 Zeichen, stabil,
 * nie wiederverwenden → AUSGEMUSTERTE_IDS). Eigene: 'eigen_<uuid>'.
 */
export interface KatalogKnoten {
    id: string;
    typ: KnotenTyp;
    /** null nur bei typ 'kategorie'. */
    eltern: string | null;
    /**
     * Beschriftung.
     * - kategorie/gruppe: Tortenstück, 1–2 Zeilen getrennt durch '\n', je Zeile ≤ 11 Zeichen.
     * - mangel: Kachel-Kurztext, ≤ 28 Zeichen, eine Zeile (die Kachel bricht selbst um).
     */
    kurz: string;
    /** Voller Name (kategorie/gruppe) für Kopfzeile, Chip, Filter; ≤ 32 Zeichen. Fehlt → `kurz` ohne '\n'. */
    name?: string;
    /** Piktogramm-Schlüssel (PIKTO_SCHLUESSEL). Fehlt → vom Elternknoten geerbt. */
    pikto?: string;
    /**
     * Nur mangel, nur Standard: interne Eintrags-IDs des Gesamtkatalogs; die
     * erste liefert die Texte (weitere sind ungenutzt). Eigene Knoten der Firma
     * tragen keine — ihre Texte stehen inline.
     */
    quelle?: string[];
    /** Nur mangel: überschreibt die Überschrift des Eintrags. */
    titel?: string;
    /** Nur mangel: überschreibt die Beschreibung des Eintrags. */
    text?: string;
    /** Nur mangel: überschreibt die gekürzte Vorschrift des Eintrags. */
    vorschrift?: string;
    dringlichkeit?: Dringlichkeit;
    /** Bei dringlichkeit 'frist': Tage ab heute, 1–90. */
    fristTage?: number;
    /** Review-Status (nur Standard). Fehlt = 'entwurf'. */
    status?: KnotenStatus;
    /** KATALOG_VERSION, mit der der Knoten dazukam (Merker „neu“ im Admin). */
    seit?: number;
    /**
     * Englische Fassung (28.09., nur Firmen-Overlay: geänderte Felder und
     * eigene Knoten). Fehlt sie, gilt die englische Standardübersetzung,
     * sonst der deutsche Text als Rückfall. Längenregeln wie deutsch.
     */
    kurzEn?: string;
    nameEn?: string;
    /** Nur mangel. */
    titelEn?: string;
    /** Nur mangel. */
    textEn?: string;
}

// ─── Englische Fassung (28.09.) ──────────────────────────────────────────────

/**
 * Sprache des Katalogs: 'en' nur bei englischer Berichtssprache, sonst
 * deutsch ('de' und 'de/en' — zweisprachige Berichte werden deutsch erfasst).
 */
export type KatalogSprache = 'de' | 'en';

/** Englische Beschriftung eines Standardknotens (standard/englisch.generated.json). */
export interface EnglischerKnoten {
    kurz: string;
    /** Nur wo der deutsche Knoten einen `name` trägt. */
    name?: string;
    /** Nur wo der deutsche Knoten eigene Texte trägt. */
    titel?: string;
    text?: string;
}

/** Englischer Auszug eines Eintrags (nur die Einträge des Kernauszugs). */
export interface EnglischerAuszug {
    titel: string;
    text: string;
    /** Englische Kurzform der gekürzten Vorschrift (vorschriftEnglisch). */
    vorschriftKurz: string;
}

/** Inhalt von standard/englisch.generated.json (erzeugt, nie von Hand ändern). */
export interface EnglischerStandard {
    /** Knoten-ID → englische Beschriftung. */
    knoten: Record<string, EnglischerKnoten>;
    /** Eintrags-ID → englischer Auszug. */
    auszug: Record<string, EnglischerAuszug>;
}

/** Inhalt von standard/index.ts. */
export interface StandardKatalog {
    version: number;
    knoten: KatalogKnoten[];
    /** Eintrags-ID → Auszug, nur die Einträge, auf die der Baum verweist. */
    auszug: Record<string, KatalogAuszug>;
    /** Abschnitts-ID → Kategorie-ID (für ALLE Abschnitte des Gesamtkatalogs). */
    abschnittZuordnung: Record<string, string>;
    /** Englische Fassung der Torte (fehlt → auch bei 'en' deutsch). */
    englisch?: EnglischerStandard;
}

// ─── Firmen-Overlay (profiles.settings.mangelKatalog) ───────────────────────

/** Felder, die das Overlay an einem Standardknoten ändern darf (Positivliste). */
export type AenderbaresFeld =
    | 'kurz' | 'name' | 'pikto' | 'titel' | 'text' | 'vorschrift' | 'dringlichkeit' | 'fristTage'
    | 'kurzEn' | 'nameEn' | 'titelEn' | 'textEn';

export const AENDERBARE_FELDER: readonly AenderbaresFeld[] = Object.freeze([
    'kurz', 'name', 'pikto', 'titel', 'text', 'vorschrift', 'dringlichkeit', 'fristTage',
    'kurzEn', 'nameEn', 'titelEn', 'textEn',
]);

export interface KnotenAenderung {
    /** KATALOG_VERSION zum Zeitpunkt der Änderung. */
    rev: number;
    felder: Partial<Pick<KatalogKnoten, AenderbaresFeld>>;
}

export interface KatalogOverlay {
    format: 1;
    /** Höchste KATALOG_VERSION, die der Admin im Editor gesehen hat. */
    gesehenBisVersion?: number;
    /** Ausgeblendete Knoten (Standard oder eigen); vererbt sich auf Kinder. */
    ausgeblendet: string[];
    geaendert: Record<string, KnotenAenderung>;
    /**
     * Eigene Knoten, id 'eigen_<uuid>', eltern = vorhandener Knoten (oder null
     * bei kategorie). Texte stehen inline; kein Verweis auf einen Eintrag des
     * Gesamtkatalogs (`quelle` wird beim Lesen verworfen).
     */
    eigene: KatalogKnoten[];
    /** Elternteil-ID ('' = Wurzel) → Reihenfolge der Kinder. Fehlende Kinder hinten dran. */
    reihenfolge: Record<string, string[]>;
    /**
     * Automatische Reihenfolge nach Häufigkeit (28.09.) abgeschaltet. Nur
     * `false` wird gespeichert; fehlt das Feld, gilt „an“.
     */
    nachHaeufigkeit?: false;
}

// ─── Aufgelöster Katalog (Laufzeit) ─────────────────────────────────────────

/** Was ein gewählter Standardmangel in den Eintrag schreibt. */
export interface MangelVorlage {
    /** Interne Eintrags-ID, falls die Vorlage aus dem Gesamtkatalog stammt — nie anzeigen, nie speichern. */
    eintragId?: string;
    titel: string;
    text: string;
    /** Gekürzte Vorschrift — die kommt in den Bericht. */
    vorschrift: string;
    dringlichkeit?: Dringlichkeit;
    fristTage?: number;
    /** Titel/Text enthalten eine Lücke („……“, „konkretisieren“ …) → gelber Hinweis. */
    platzhalter: boolean;
}

export interface AufgeloesterKnoten {
    id: string;
    typ: KnotenTyp;
    eltern: string | null;
    kurz: string;
    name: string;
    /** Wirksames Piktogramm (geerbt, nie leer; Rückfall 'allgemein'). */
    pikto: string;
    /** ID der Kategorie (Ebene 1) dieses Knotens. */
    kategorieId: string;
    /** Namen von der Kategorie bis zu diesem Knoten, z. B. ['Absturz', 'Seitenschutz', 'Bordbrett fehlt']. */
    pfad: string[];
    herkunft: 'standard' | 'eigen';
    /** Overlay hat Felder geändert. */
    geaendert: boolean;
    /** Standardknoten neuer als overlay.gesehenBisVersion. */
    neu: boolean;
    /** Nur mangel. */
    vorlage?: MangelVorlage;
}

export interface AufgeloesterKatalog {
    version: number;
    /** Alle SICHTBAREN Knoten. */
    knoten: ReadonlyMap<string, AufgeloesterKnoten>;
    /** Eltern-ID ('' = Wurzel) → sichtbare Kinder in wirksamer Reihenfolge. */
    kinder: ReadonlyMap<string, readonly string[]>;
    /** Ausgeblendete Knoten (samt vererbter), nur für den Admin-Editor. */
    ausgeblendet: ReadonlySet<string>;
}

// ─── Auswahl → Eintrag ──────────────────────────────────────────────────────

/** Ergebnis der Auswahl (Torte, Kachel, Suche, Zuletzt, Favorit oder Freitext-Kachel). */
export interface KatalogAuswahl {
    art: 'knoten' | 'eintrag' | 'freitext';
    /** Knoten-ID (Standard oder eigen_…); null bei einem Treffer aus dem Gesamtkatalog und bei Freitext. */
    katalogKey: string | null;
    /** Kategorie-ID (Ebene 1). */
    katalogKategorie: string | null;
    /** null bei Freitext. */
    vorlage: MangelVorlage | null;
    /** Namen für Chip/Tooltip, z. B. ['Absturz', 'Seitenschutz']. */
    pfad: string[];
    pikto: string;
    /**
     * Mehrfachauswahl (Paket L, 30.09.): Zahl der zusammengeführten Punkte
     * (≥ 2, auswahlZusammenfuehren); fehlt bei einem einzelnen Punkt. Wird
     * nicht gespeichert, nur der Chip zeigt sie.
     */
    anzahl?: number;
}

// ─── Häufigkeit (Reihenfolge nach Nutzung, 28.09.) ─────────────────────────

/**
 * Wie oft die Firma einen Knoten bzw. eine Kategorie gewählt hat (RPC
 * mangel_katalog_haeufigkeit, Nr. 132) — als Gewicht: jüngere Mängel zählen
 * mehr (Halbwertszeit 90 Tage).
 */
export interface KatalogHaeufigkeit {
    /** Knoten-ID (katalog_key) → Gewicht. */
    knoten: ReadonlyMap<string, number>;
    /** Kategorie-ID (katalog_kategorie) → Gewicht; zählt auch Suche und Freitext. */
    kategorien: ReadonlyMap<string, number>;
}

/** Merkliste-Schlüssel: 'knoten:<id>' oder 'eintrag:<eintragId>'. */
export type MerkSchluessel = string;
